import z from "zod";
import {
  CreateShelfPayloadSchema,
  CreateShelfRowPalletPayloadSchema,
  CreateShelfRowPayloadSchema,
  UpdateShelfBayLevelNamePayloadSchema,
  UpdateShelfNamePayloadSchema,
} from "../hooks/shelveHooks";
import { apiClient } from "../commonApis/apiClient";
import { useMutation } from "@tanstack/react-query";
import { fetch5 } from "../commonApis/commonApis";
const url = process.env.REACT_APP_BASE_URL;
export interface Shelf {
  id: string;
  warehouseId: string;
  location: { lat: number; lng: number };
  name?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  storageType?: "B2C" | "B2B";
}

export interface ShelfRow {
  id: string;
  shelfId: string;
  warehouseId: string;
  name?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ShelfRowWhRow {
  id: string;
  whAreaId: string;
  warehouseId: string;
  name?: string;
  status?: "ACTIVE" | "INACTIVE";
  barcode?: string;
  barcodeId?: string;
  barcodeLabel?: string;
  createdAt?: string;
  updatedAt?: string;
  area?: {
    id: string;
    name: string;
  };
}

export interface ShelfRowBay {
  id: string;
  rowId: string;
  name?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ShelfRowFloor {
  id: string;
  rowId: string;
  name?: string;
  isActive?: boolean;
  whRackId?: string | null;
  position?: "Start" | "End" | null;
  after_wh_row_id?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ShelfRowFloorWithPallets extends ShelfRowFloor {
  pallet?: ShelfRowPallet[];
}

export interface ShelfRowPallet {
  id: string;
  rowId: string;
  name?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export type CreateShelfPayload = z.infer<typeof CreateShelfPayloadSchema>;

export interface UpdateShelfPayload {
  location?: { lat: number; lng: number };
  name?: string;
  isActive?: boolean;
}

export type CreateShelfRowPayload = z.infer<typeof CreateShelfRowPayloadSchema>;

export interface UpdateShelfRowPayload {
  whAreaId?: string;
  whRackId?: string;
  whRowId?: string;
  name?: string;
  isActive?: boolean;
  levels?: any;
}

export interface CreateShelfRowWhRowPayload {
  warehouseId: string;
  whAreaId: string;
  name: string;
  rowNumbers: string;
}

export interface UpdateShelfRowWhRowPayload {
  name?: string;
  status?: "ACTIVE" | "INACTIVE";
}

export interface CreateShelfRowBayBinPayload {
  // bayId: string;
  warehouseId: string;
  shelveId: string;
  rackInstanceId: string;
  clientId: string;
  // storageType: "B2C" | "B2B";
  bins: {
    rackLevelId: string;
    rackLevel: string;
    binType: string;
    binNumbers: string;
  }[];
}
export interface UpdateShelfRowBayBinPayload {
  name?: string;
  id?: string;
}

export interface AddLevelInBayPayload {
  name: string;
  bayId: string;
}

export interface CreateShelfRowFloorPayload {
  whAreaId: string;
  floorName: string;
  floorNumbers: string;
  whRowId?: string;
  whRackId?: string;
  position?: string;
}

export interface UpdateShelfRowFloorPayload {
  name?: string;
  id?: string;
}

export type CreateShelfRowPalletPayload = z.infer<
  typeof CreateShelfRowPalletPayloadSchema
>;
export type UpdateShelfRowBayPayload = z.infer<
  typeof UpdateShelfNamePayloadSchema
>;
export type UpdateShelfBayLevelNamePayload = z.infer<
  typeof UpdateShelfBayLevelNamePayloadSchema
>;

export interface UpdateShelfRowPalletPayload {
  name?: string;
  id?: string;
}

export interface BulkOperationPayload {
  ids: string[];
  isActive?: boolean;
}
 

interface ApiResponse {
  success: boolean;
  data: any;
  message?: string;
}
interface UpdateShelveStatusPayload {
  id: string;
  status: "ACTIVE" | "INACTIVE";
  model: "area" | "rack" | "level" | "bin" | "pallet" | "floor" | "row";
}

export const useUpdateShelveStatusMutation = () => {
  return useMutation({
    mutationFn: async (payload: UpdateShelveStatusPayload) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/admin/shelve/update/status`,
        "PATCH",
        {
          type: payload.model,
          id: payload.id,
          status: payload.status,
        }
      );

      if (!res.success) {
        throw new Error(res.message || "Status update failed");
      }
      return res;
    },
  });
};

export const shelfApi = {
  // Shelf CRUD
  createShelf: (
    payload: CreateShelfPayload | FormData,
    isFormData: boolean = false,
  ) => apiClient.post<Shelf>("/api/admin/shelve", payload, isFormData),
  getShelves: (page: number, searchFor: string) =>
    apiClient.get<Shelf[]>(`/api/shelve?page=${page}&searchFor=${searchFor}`),
  getAllShelves: (warehouseId: string) =>
    apiClient.get<Shelf[]>(
      `/api/admin/shelve/all/${warehouseId}`,
    ),
  getShelfById: (id: string) => apiClient.get<Shelf>(`/api/shelve/${id}`),
  updateShelf: (id: string, payload: UpdateShelfPayload) =>
    apiClient.patch<Shelf>(`/api/admin/shelve/${id}`, payload),
  updateShelfFormData: (id: string, payload: Record<string, any>) =>
    apiClient.patch<Shelf>(`/api/shelve/${id}`, payload, true),
  deleteShelf: (id: string, payload?: Record<string, any>) =>
    apiClient.delete<void>(`/api/shelve/${id}`, payload),
  bulkUpdateShelves: (payload: BulkOperationPayload) =>
    apiClient.patch<void>("/api/shelve/bulk", payload),
  downloadShelveBarcode: (warehouseId: string, shelveId: string) =>
    apiClient.get<{ pdfUrl: string }>(
      `/api/admin/shelve/${shelveId}/download-barcode`,
    ),
  // Shelf Row CRUD
  createShelfRow: (
    payload: CreateShelfRowPayload | FormData,
    isFormData: boolean = false,
  ) => apiClient.post<ShelfRow>("/api/admin/shelve/rack-rows", payload, isFormData),
  getShelfRows: (shelfId: string, warehouseId: string) =>
    apiClient.get<ShelfRow[]>(
      `/api/admin/shelve/rack-rows/${shelfId}/racks`,
    ),
  getShelfRowById: (id: string) =>
    apiClient.get<ShelfRow>(`/api/shelve/row/${id}`),
  updateShelfRow: (id: string, payload: UpdateShelfRowPayload) => {
    const { whAreaId, ...rest } = payload;
    const areaId = whAreaId || id;
    return apiClient.patch<ShelfRow>(`/api/admin/shelve/rack-rows/${areaId}`, rest);
  },
  updateShelfRowFormData: (id: string, payload: Record<string, any>) =>
    apiClient.patch<ShelfRow>(`/api/shelve/row/${id}`, payload, true),
  deleteShelfRow: (id: string, payload?: Record<string, any>) =>
    apiClient.delete<void>(`/api/shelve/row/rowInstance/${id}`, payload),
  bulkUpdateShelfRows: (payload: BulkOperationPayload) =>
    apiClient.patch<void>("/api/shelve/row/bulk", payload),

  // Update Shelf Row Bay CRUD
  updateShelfRowBay: (id: string, payload?: Record<string, any>) =>
    apiClient.patch<void>(`/api/shelve/row/rowInstance/bay/${id}`, payload),
  deleteShelfRowBay: (id: string, payload?: Record<string, any>) =>
    apiClient.delete<void>(`/api/shelve/row/rowInstance/bay/${id}`, payload),

  // Shelf Row Bay Level CRUD
  addShelfRowBayLevel: (id: string, payload: AddLevelInBayPayload) =>
    apiClient.put<ShelfRow>(`/api/shelve/row/rowInstance/bay/${id}`, payload),
  updateShelfRowBayLevel: (
    id: string,
    payload?: UpdateShelfBayLevelNamePayload,
  ) =>
    apiClient.patch<void>(
      `/api/shelve/row/rowInstance/bay/bayLevel/${payload?.id}`,
      payload,
    ),
  deleteShelfRowBayLevel: (id: string, payload?: Record<string, any>) =>
    apiClient.delete<void>(
      `/api/shelve/row/rowInstance/bay/bayLevel/${id}`,
      payload,
    ),

  // Shelf Row Bay Bin CRUD
  createShelfRowBayBin: (
    payload: CreateShelfRowBayBinPayload | FormData,
    isFormData: boolean = false,
  ) =>
    apiClient.post<ShelfRowBay>(
      "/api/shelve/create-rack-level-bin",
      payload,
      isFormData,
    ),
  
  // Rack Level Bins API
  createRackLevelBins: (
    payload: CreateShelfRowBayBinPayload | FormData,
    isFormData: boolean = false,
  ) =>
    apiClient.post<ShelfRowBay>(
      "/api/admin/shelve/rack/level/bins",
      payload,
      isFormData,
    ),
  
  // Update existing rack level bins with PATCH method and whLevelId
  updateRackLevelBins: (
    payload: CreateShelfRowBayBinPayload | FormData,
    isFormData: boolean = false,
  ) =>
    apiClient.patch<ShelfRowBay>(
      "/api/admin/shelve/rack/level/bins",
      payload,
      isFormData,
    ),
  getShelfRowBayBins: (rowId: string) =>
    apiClient.get<ShelfRowBay[]>(`/api/shelve/row/${rowId}/bay-bins`),
  getShelfRowBayBinById: (id: string) =>
    apiClient.get<ShelfRowBay>(`/api/shelve/row/bay-bin/${id}`),
  updateShelfRowBayBin: (id: string, payload: UpdateShelfRowBayBinPayload) =>
    apiClient.patch<ShelfRowBay>(
      `/api/shelve/row/rowInstance/bay/bayLevel/bin/${id}`,
      payload,
    ),
  updateShelfRowBayBinFormData: (id: string, payload: Record<string, any>) =>
    apiClient.patch<ShelfRowBay>(
      `/api/shelve/row/bay-bin/${id}`,
      payload,
      true,
    ),
  deleteShelfRowBayBin: (id: string, payload?: Record<string, any>) =>
    apiClient.delete<void>(
      `/api/shelve/row/rowInstance/bay/bin/${id}`,
      payload,
    ),
  bulkUpdateShelfRowBayBins: (payload: BulkOperationPayload) =>
    apiClient.patch<void>("/api/shelve/row/bay-bin/bulk", payload),

  // Shelf Row Floor CRUD
  createShelfRowFloor: (
    payload: CreateShelfRowFloorPayload | FormData,
    isFormData: boolean = false,
  ) =>
    apiClient.post<ShelfRowFloor>(
      "/api/admin/shelve/floors",
      payload,
      isFormData,
    ),
  getShelfAreaFloors: (areaId: string) =>
    apiClient.get<ShelfRowFloorWithPallets[]>(`/api/admin/shelve/floors/${areaId}`),
  getShelfRowFloorById: (id: string) =>
    apiClient.get<ShelfRowFloor>(`/api/shelve/row/floor/${id}`),
  updateShelfRowFloor: (id: string, payload: UpdateShelfRowFloorPayload) =>
    apiClient.patch<ShelfRowFloor>(
      `/api/admin/shelve/floors/${id}`,
      payload,
    ),
  updateShelfRowFloorSlot: (id: string, payload: UpdateShelfRowFloorPayload) =>
    apiClient.patch<ShelfRowFloor>(
      `/api/shelve/row/rowInstance/floor/floorSlot/${id}`,
      payload,
    ),
  updateShelfRowFloorFormData: (id: string, payload: Record<string, any>) =>
    apiClient.patch<ShelfRowFloor>(
      `/api/shelve/row/floor/${id}`,
      payload,
      true,
    ),
  deleteShelfRowFloor: (id: string, payload?: Record<string, any>) =>
    apiClient.delete<void>(`/api/shelve/row/rowInstance/floor/${id}`, payload),
  bulkUpdateShelfRowFloors: (payload: BulkOperationPayload) =>
    apiClient.patch<void>("/api/shelve/row/floor/bulk", payload),

  // Shelf Row Pallet CRUD
  createShelfRowPallet: (
    payload: CreateShelfRowPalletPayload | FormData,
    isFormData: boolean = false,
  ) =>
    apiClient.post<ShelfRowPallet>(
      "/api/shelve/row/create-pallet",
      payload,
      isFormData,
    ),
  
  // Rack Level Pallets API
  createRackLevelPallets: (
    payload: CreateShelfRowPalletPayload | FormData,
    isFormData: boolean = false,
  ) =>
    apiClient.post<ShelfRowPallet>(
      "/api/admin/shelve/rack/level/pallets",
      payload,
      isFormData,
    ),
  updateRackLevelPallets: (
    payload: CreateShelfRowPalletPayload | FormData,
    isFormData: boolean = false,
  ) =>
    apiClient.patch<ShelfRowPallet>(
      "/api/admin/shelve/rack/level/pallets",
      payload,
      isFormData,
    ),
  createFloorPallets: (
    payload: any,
    isFormData: boolean = false,
  ) =>
    apiClient.post<any>(
      "/api/admin/shelve/floor/pallets",
      payload,
      isFormData,
    ),
  updateFloorPallets: (
    payload: any,
    isFormData: boolean = false,
  ) =>
    apiClient.patch<any>(
      "/api/admin/shelve/floor/pallets",
      payload,
      isFormData,
    ),
  getShelfRowPallets: (rowId: string) =>
    apiClient.get<ShelfRowPallet[]>(`/api/shelve/row/${rowId}/pallets`),
  getShelfRowPalletById: (id: string) =>
    apiClient.get<ShelfRowPallet>(`/api/shelve/row/pallet/${id}`),
  updateShelfRowPallet: (id: string, payload: UpdateShelfRowPalletPayload) =>
    apiClient.patch<ShelfRowPallet>(
      `/api/shelve/row/rowInstance/pallet/${id}`,
      payload,
    ),
  updateShelfRowPalletSlot: (
    id: string,
    payload: UpdateShelfRowPalletPayload,
  ) =>
    apiClient.patch<ShelfRowPallet>(
      `/api/shelve/row/rowInstance/pallet/palletSlot/${id}`,
      payload,
    ),
  updateShelfRowPalletFormData: (id: string, payload: Record<string, any>) =>
    apiClient.patch<ShelfRowPallet>(
      `/api/shelve/row/pallet/${id}`,
      payload,
      true,
    ),
  deleteShelfRowPallet: (id: string, payload?: Record<string, any>) =>
    apiClient.delete<void>(`/api/shelve/row/rowInstance/pallet/${id}`, payload),
  bulkUpdateShelfRowPallets: (payload: BulkOperationPayload) =>
    apiClient.patch<void>("/api/shelve/row/pallet/bulk", payload),

  // Shelf Row WhRow CRUD
  createShelfRowWhRow: (
    payload: CreateShelfRowWhRowPayload | FormData,
    isFormData: boolean = false,
  ) => apiClient.post<ShelfRowWhRow>("/api/admin/shelve/wh-rows", payload, isFormData),
  
  getShelfRowWhRows: (whAreaId: string) =>
    apiClient.get<ShelfRowWhRow[]>(`/api/admin/shelve/wh-rows/${whAreaId}`),
  
  updateShelfRowWhRowName: (rowId: string, payload: { name?: string; status?: "ACTIVE" | "INACTIVE" }) =>
    apiClient.patch<ShelfRowWhRow>(`/api/admin/shelve/wh-rows/${rowId}`, payload),
  
  deleteShelfRowWhRow: (id: string, payload?: Record<string, any>) =>
    apiClient.delete<void>(`/api/admin/shelve/wh-rows/${id}`, payload),
  
  bulkUpdateShelfRowWhRows: (payload: BulkOperationPayload) =>
    apiClient.patch<void>("/api/admin/shelve/wh-rows/bulk", payload),

  // Admin Rack Rows CRUD
  updateAdminRacksWithLevels: (areaId: string, payload: UpdateShelfRowPayload) =>
    apiClient.patch<any>(`/api/admin/shelve/rack-rows/${areaId}`, payload),

  getLocationInventory: (locationId: string) =>
    apiClient.get<any>(`/api/product-items-inventory/${locationId}`),

  fetchShelvesTemplate: () =>
    apiClient.get<any>("/api/templates/shelves"),

  bulkUploadShelves: (payload: FormData) =>
    apiClient.post<any>("/api/admin/shelve/bulk-upload", payload, true),
};
